import type { Job } from './types';

export type PollJobOptions = {
  /** first wait before the initial poll (ms) */
  initialDelayMs?: number;
  /** cap for the backoff (ms) */
  maxDelayMs?: number;
  /** multiplier applied after every poll */
  factor?: number;
  /** give up after this long (ms) */
  timeoutMs?: number;
  signal?: AbortSignal;
  onProgress?: (job: Job) => void;
  /** injectable clock for tests */
  sleep?: (ms: number) => Promise<void>;
};

export class JobFailedError extends Error {
  constructor(
    readonly job: Job,
    message = job.error_message ?? 'Job failed',
  ) {
    super(message);
    this.name = 'JobFailedError';
  }
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Poll a `Job` (`GET /jobs/{jobId}` or the public share variant) with exponential backoff until it is
 * `done` (resolves with the job, `result.url` carries the signed download) or `failed` (rejects with
 * `JobFailedError`). The API contract has no WebSocket event for zip jobs, so this is the only path.
 */
export async function pollJob(fetchJob: () => Promise<Job>, opts: PollJobOptions = {}): Promise<Job> {
  const { initialDelayMs = 800, maxDelayMs = 5000, factor = 1.6, timeoutMs = 15 * 60_000, signal, onProgress, sleep = defaultSleep } = opts;
  const started = Date.now();
  let delay = initialDelayMs;
  for (;;) {
    if (signal?.aborted) throw new DOMException('Job polling aborted', 'AbortError');
    const job = await fetchJob();
    if (job.state === 'done') return job;
    if (job.state === 'failed') throw new JobFailedError(job);
    onProgress?.(job);
    if (Date.now() - started > timeoutMs) throw new Error('Timed out waiting for the job');
    await sleep(delay);
    delay = Math.min(maxDelayMs, delay * factor);
  }
}

/** Save a signed URL through a transient `<a download>`; sync with the user gesture, so popup blockers stay quiet. */
export function saveUrl(url: string, filename?: string | null): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
